import { Box, Grid, Typography } from '@mui/material'
import { useCallback, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { BreadCrumb, SnackSimple } from '../../../../components'
import { CustomPaper } from '../../../../components/CustomPaper'
import { useContextState } from '../../../../hooks/theme'
import Loader from '../../../../components/loader'
import { RequestErrors, Types } from '../../../../utils/enums'
import { handleAllErrors } from '../../../../utils/methods/handleErrors'
import { httpFetch } from '../../../../utils/crud'
import { actionPlansServices } from '../../../../service/actionPlans'
import LeftFragment from './fragments/left'
import { QuestionWithEvidencesSection } from '../form/components/questionWithEvidencesSection'
import { mapAutomaticTempToDetail } from './methods'
import { useStyles } from './styles'
import type { ActionPlanToGenerateDetail } from './types'

const ToGenerateDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { colors } = useContextState()
  const styles = useStyles()
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<ActionPlanToGenerateDetail | null>(null)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: Types.SUCCESS,
  })

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const handleGenerateActionPlan = () => {
    if (id) {
      navigate(`/dashboard/action-plan/to-generate/${id}/create`)
    }
  }

  const loadDetail = useCallback(async () => {
    if (!id) return
    setIsLoading(true)
    const response = await actionPlansServices(httpFetch).getAutomaticTempById(Number(id))
    if (response.errors) {
      setSnackbar({
        open: true,
        message: handleAllErrors(response.errors[0]?.code as RequestErrors, t),
        type: Types.ERROR,
      })
      setData(null)
    } else {
      setData(mapAutomaticTempToDetail(response))
    }
    setIsLoading(false)
  }, [id, t])

  useLayoutEffect(() => {
    loadDetail()
  }, [loadDetail])

  if (isLoading || !data) {
    return (
      <Box sx={styles.container}>
        <BreadCrumb
          removeBreadcrumb={['created', 'to-generate']}
          replacePath={{ 'action-plan': '/dashboard/action-plan/to-generate' }}
          replaceLabels={{ 'action-plan': 'actionPlanToGenerate', 'to-generateEdit': 'actionPlanToGenerateEdit' }}
        />
        <Loader sx={{ height: 'calc(100% - 100px)' }} />
      </Box>
    )
  }

  return (
    <Box sx={styles.container}>
      <BreadCrumb
        removeBreadcrumb={['created', 'to-generate']}
        replacePath={{ 'action-plan': '/dashboard/action-plan/to-generate' }}
        replaceLabels={{ 'action-plan': 'actionPlanToGenerate', 'to-generateEdit': 'actionPlanToGenerateEdit' }}
      />

      <CustomPaper sx={styles.containerFragments}>
        <Typography sx={styles.title} >{t('actionPlan.actionPlanToGenerateTitle')}</Typography>
        <Grid container columnSpacing={3} sx={styles.mainGrid}>
          <Grid item xl={3} lg={4} md={12} sm={12} xs={12} sx={{ height: '100%', margin: '25px 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <LeftFragment
              mode={data.mode}
              store={data.store}
              assigningUser={data.assigningUser}
              assignedUser={data.assignedUser}
              approvers={data.approvers}
              onGenerateActionPlan={handleGenerateActionPlan}
            />
          </Grid>

          <Grid item xl={9} lg={8} md={12} sm={12} xs={12} sx={{ height: '100%', margin: '24px 0', paddingRight: '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={styles.rightPanel}>
              <QuestionWithEvidencesSection
                questions={data.questionList}
                detailCreation={{ questionnaire: data.templateName }}
                id={data.id}
                showGlobalActions
              />
            </Box>
          </Grid>
        </Grid>
      </CustomPaper>

      <SnackSimple
        close={handleCloseSnackbar}
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
      />
    </Box>
  )
}

export default ToGenerateDetail
